import { RepeatIcon } from "@phosphor-icons/react/dist/csr/Repeat";
import { useMemo } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Separator } from "@volca/ui/components/ui/separator";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { formatDate, sortDateDescending } from "@/core/utils/date";
import { formatMoneyCents } from "@/core/utils/money";
import { INTERNAL_TRANSACTION_TYPE } from "@/internal-transactions/constants";
import {
  useGetAccountSummary,
  useGetInternalTransactions,
} from "@/internal-transactions/query";
import { useGetPayouts } from "@/payouts/query";
import { PaymentsSummaryInfo } from "./payments-summary-info";

interface PaymentsSummaryProps {
  businessId?: string | null;
  onAutoRefillClick?: () => void;
}

const PaymentsSummary = ({
  businessId,
  onAutoRefillClick,
}: PaymentsSummaryProps) => {
  const { data: walletInfo, isLoading: isWalletInfoLoading } =
    useGetAccountSummary(businessId);
  const { data: referralPayouts = [], isLoading: isReferralPayoutsLoading } =
    useGetPayouts(businessId);
  const { data: transactions = [], isLoading: isTransactionsLoading } =
    useGetInternalTransactions(businessId);

  const lastReferralPayout = useMemo(
    () =>
      [...referralPayouts].sort((a, b) =>
        sortDateDescending(a.created_at, b.created_at),
      )[0] || null,
    [referralPayouts],
  );

  const lastDeposit = useMemo(
    () =>
      [...transactions]
        .filter((t) => t.type === INTERNAL_TRANSACTION_TYPE.deposit)
        .sort((a, b) => sortDateDescending(a.created_at, b.created_at))[0] ||
      null,
    [transactions],
  );

  const hasWalletInfo = !isWalletInfoLoading && walletInfo;
  const hasAutoRefill = walletInfo?.settings?.auto_refill_active;

  return (
    <Stack
      direction="row"
      align="center"
      gap={4}
      className="w-full flex-wrap gap-y-0 px-6"
    >
      <Stack className="p-3 pr-8 pl-4" gap={0} align="center">
        {isWalletInfoLoading && (
          <>
            <Skeleton className="h-8 w-[146px]" />
            <Skeleton className="mt-1 h-4 w-25" />
          </>
        )}
        {hasWalletInfo && (
          <>
            <Stack direction="row" align="center" gap={1}>
              <Text variant="display" className="text-accent-cyan">
                {formatMoneyCents(walletInfo?.wallet_balance || 0)}
              </Text>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={onAutoRefillClick}
                      aria-label={
                        hasAutoRefill
                          ? "Auto-refill enabled"
                          : "Set up auto-refill"
                      }
                    />
                  }
                >
                  <RepeatIcon
                    className={
                      hasAutoRefill
                        ? "h-6 w-6 text-success"
                        : "h-6 w-6 text-subtle"
                    }
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {hasAutoRefill
                      ? "Auto-refill enabled"
                      : "Set up auto-refill"}
                  </p>
                </TooltipContent>
              </Tooltip>
            </Stack>
            <Text variant="label" tone="muted">
              Account balance
            </Text>
          </>
        )}
      </Stack>

      {hasWalletInfo && (
        <Separator orientation="vertical" className="hidden h-full sm:block" />
      )}

      <PaymentsSummaryInfo
        isLoading={isReferralPayoutsLoading}
        value={formatMoneyCents(lastReferralPayout?.amount || 0)}
        label="Last referral payout"
        date={formatDate(lastReferralPayout?.created_at || "")}
        show={!!lastReferralPayout}
      />

      {!!lastReferralPayout && (
        <Separator orientation="vertical" className="hidden h-full sm:block" />
      )}

      <PaymentsSummaryInfo
        isLoading={isTransactionsLoading}
        value={formatMoneyCents(lastDeposit?.amount || 0)}
        label="Last deposit"
        date={formatDate(lastDeposit?.created_at || "")}
        show={!!lastDeposit}
      />
    </Stack>
  );
};

export default PaymentsSummary;
